/**
 * Servicio de Fingerprinting
 * Genera un identificador único del navegador y gestiona cookie tokens
 */
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Clave para almacenar cookie token en localStorage
const COOKIE_TOKEN_KEY = 'plaza_express_cookie_token';

/**
 * Genera un fingerprint único del navegador mejorado
 * Combina múltiples características para mayor precisión
 * @returns {Promise<string>} Hash del fingerprint
 */
export const generateFingerprint = async () => {
  try {
    // Inicializar FingerprintJS con configuración mejorada
    const fp = await FingerprintJS.load({
      debug: false,
    });
    
    // Obtener resultado del fingerprinting
    const result = await fp.get();
    
    // Combinar visitorId con componentes adicionales para mayor unicidad
    const components = result.components;
    
    // Extraer características clave
    // eslint-disable-next-line no-restricted-globals
    const screenWidth = window.screen.width;
    // eslint-disable-next-line no-restricted-globals
    const screenHeight = window.screen.height;
    const screenResolution = `${screenWidth}x${screenHeight}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;
    const platform = navigator.platform;
    const hardwareConcurrency = navigator.hardwareConcurrency || 'unknown';
    
    // Crear string combinado para hash adicional
    const additionalData = [
      screenResolution,
      timezone,
      language,
      platform,
      hardwareConcurrency,
      components.canvas?.value || '',
      components.webgl?.value || '',
    ].join('|');
    
    // Combinar visitorId con datos adicionales y crear hash
    const combined = `${result.visitorId}|${additionalData}`;
    
    // Hash simple (en producción podría usar crypto.subtle)
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return Math.abs(hash).toString(36) + result.visitorId.substring(0, 16);
  } catch (error) {
    console.error('Error al generar fingerprint:', error);
    // Fallback: generar un ID basado en características del navegador
    return generateFallbackFingerprint();
  }
};

/**
 * Genera un fingerprint de respaldo si FingerprintJS falla
 * @returns {string} Hash de respaldo
 */
const generateFallbackFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('Plaza Express Fingerprint', 2, 2);
  
  // eslint-disable-next-line no-restricted-globals
  const screenWidth = window.screen.width;
  // eslint-disable-next-line no-restricted-globals
  const screenHeight = window.screen.height;
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    `${screenWidth}x${screenHeight}`,
    new Date().getTimezoneOffset(),
    canvas.toDataURL(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.platform,
  ].join('|');
  
  // Hash simple (en producción usar crypto.subtle)
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convertir a 32bit integer
  }
  
  return Math.abs(hash).toString(36);
};

/**
 * Obtiene o crea un cookie token único
 * El token persiste en localStorage y se usa junto con el fingerprint
 * @returns {string} Cookie token
 */
export const getOrCreateCookieToken = () => {
  try {
    // Intentar obtener token existente
    let token = localStorage.getItem(COOKIE_TOKEN_KEY);
    
    if (!token) {
      // Generar nuevo token si no existe
      token = generateCookieToken();
      localStorage.setItem(COOKIE_TOKEN_KEY, token);
    }
    
    return token;
  } catch (error) {
    console.error('Error al obtener/crear cookie token:', error);
    // Fallback: generar token temporal
    return generateCookieToken();
  }
};

/**
 * Genera un nuevo cookie token
 * @returns {string} Token único
 */
const generateCookieToken = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
};

/**
 * Obtiene ambos identificadores (fingerprint y cookie token)
 * @returns {Promise<{fingerprint: string, cookieToken: string}>}
 */
export const getIdentifiers = async () => {
  const [fingerprint, cookieToken] = await Promise.all([
    generateFingerprint(),
    Promise.resolve(getOrCreateCookieToken()),
  ]);
  
  return {
    fingerprint,
    cookieToken,
  };
};

/**
 * Limpia el cookie token (útil para testing)
 */
export const clearCookieToken = () => {
  try {
    localStorage.removeItem(COOKIE_TOKEN_KEY);
  } catch (error) {
    console.error('Error al limpiar cookie token:', error);
  }
};

